import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { LayerColors } from '@/types/canvas';
import { useTranslation } from 'react-i18next';

const LAYER_COLOR_KEYS: (keyof LayerColors)[] = [
  'background',
  'water',
  'land',
  'park',
  'forest',
  'building',
  'building_outline',
  'road_motorway',
  'road_major',
  'road_minor',
  'road_path',
  'label_text',
  'label_halo',
];
import { Label } from './ui/label';
import { Button } from './ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  ColorPicker,
  ColorPickerSelection,
  ColorPickerHue,
  ColorPickerAlpha,
  ColorPickerFormat,
  ColorPickerEyeDropper,
  ColorPickerOutput,
  ColorPickerSwatch,
} from './ui/color-picker';
import {
  getColorPresets,
  getDefaultColorPreset,
  type ColorPreset,
} from '@/lib/api';
import { FALLBACK_COLOR_PRESETS } from './ColorPresetsButtons';
// import { getDefaultColors } from '@/config/mapboxStyles';

interface ColorLayerPickerProps {
  colors: LayerColors;
  colorScheme?: string; // Pro automatické mapování presetu podle colorScheme
  colorPresetId?: string; // Aktuálně vybraný preset ID
  onColorChange: (layer: keyof LayerColors, color: string) => void;
  onReset: () => void;
  onPresetLoad?: (colors: LayerColors, presetId?: string) => void;
}

export function ColorLayerPicker({
  colors,
  colorScheme,
  colorPresetId,
  onColorChange,
  onReset,
  onPresetLoad,
}: ColorLayerPickerProps) {
  const { t } = useTranslation();
  const areLayerColorsEqual = (a?: LayerColors, b?: LayerColors) => {
    if (a === b) return true;
    if (!a || !b) return false;
    return LAYER_COLOR_KEYS.every((key) => a[key] === b[key]);
  };

  // Lokální states pro barvy jednotlivých vrstev - zdroj pravdy ve frontendu
  const [localColors, setLocalColors] = useState<LayerColors>(colors);
  const [presets, setPresets] = useState<ColorPreset[]>([]);
  const [selectedPresetId, setSelectedPresetId] = useState<string>(colorPresetId || '');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDefault, setIsLoadingDefault] = useState(false);

  // Synchronizovat lokální colors s props při změně
  useEffect(() => {
    if (!colors) return;
    if (!areLayerColorsEqual(localColors, colors)) {
      setLocalColors(colors);
    }
  }, [colors, localColors]);

  // Načtení presetů a defaultního presetu při načtení komponenty
  useEffect(() => {
    loadPresets();
    loadDefaultPreset();
  }, []);

  // Aktualizovat selectedPresetId když se změní colorPresetId z props
  useEffect(() => {
    if (colorPresetId) {
      setSelectedPresetId(colorPresetId);
    }
  }, [colorPresetId]);

  // Automaticky vybrat preset podle colorScheme když se presety načtou
  useEffect(() => {
    if (presets.length > 0 && colorScheme && !colorPresetId && !selectedPresetId) {
      // Najít preset s tagem odpovídajícím colorScheme
      const matchingPreset = presets.find(p =>
        p.tags?.includes(colorScheme) || p.name.toLowerCase() === colorScheme.toLowerCase()
      );
      if (matchingPreset) {
        setSelectedPresetId(matchingPreset.id);
        applyPresetColors(matchingPreset.colors, matchingPreset.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presets.length, colorScheme]); // Pouze když se presety načtou nebo změní colorScheme

  // Načíst defaultní preset (is_default = true) při inicializaci
  const loadDefaultPreset = async () => {
    // Pouze pokud ještě nemáme nastavené barvy z props
    if (colors && Object.keys(colors).length > 0 && Object.values(colors).some(v => v)) {
      return; // Barvy už jsou nastavené z props
    }

    setIsLoadingDefault(true);
    try {
      const result = await getDefaultColorPreset();

      if (result.success && result.data) {
        // console.log('[ColorLayerPicker] Načten defaultní preset:', result.data.name);
        applyPresetColors(result.data.colors, result.data.id);
      } else {
        console.warn('[ColorLayerPicker] Defaultní preset nenalezen:', result.error);
      }
    } catch (error) {
      console.error('[ColorLayerPicker] Chyba při načítání defaultního presetu:', error);
    } finally {
      setIsLoadingDefault(false);
    }
  };

  // Pomocná funkce pro aplikování barev z presetu na lokální state
  const applyPresetColors = (presetColors: LayerColors, presetId?: string) => {
    setLocalColors(presetColors);
    if (onPresetLoad) {
      onPresetLoad(presetColors, presetId);
    }
  };

  const loadPresets = async () => {
    setIsLoading(true);
    try {
      // console.log('[ColorLayerPicker] Načítání presetů z API...');
      const result = await getColorPresets(true); // includePrivate = true pro administrátora
      // console.log('[ColorLayerPicker] API response:', result);

      if (result.success && result.data) {
        const presetsArray = Array.isArray(result.data) ? result.data : [];
        if (presetsArray.length > 0) {
          setPresets(presetsArray);
          return;
        }
      }
      setPresets(FALLBACK_COLOR_PRESETS);
    } catch (error) {
      console.error('[ColorLayerPicker] Exception při načítání presetů:', error);
      setPresets(FALLBACK_COLOR_PRESETS);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadPreset = async (presetId: string) => {
    if (!presetId) return;

    const preset = presets.find(p => p.id === presetId);
    if (preset) {
      applyPresetColors(preset.colors, presetId);
      setSelectedPresetId(presetId);
    }
  };

  // Handler pro změnu barvy jednotlivé vrstvy - aktualizuje lokální state
  const handleLayerColorChange = (layer: keyof LayerColors, color: string) => {
    const updatedColors = { ...localColors, [layer]: color };
    setLocalColors(updatedColors);
    // Vyčistit vybraný preset při manuální úpravě barev
    setSelectedPresetId('');
    // Informovat parent komponentu o změně
    onColorChange(layer, color);
  };

  // Handler pro reset - obnovit barvy z aktuálně vybraného presetu nebo default
  const handleReset = async () => {
    if (selectedPresetId) {
      const preset = presets.find(p => p.id === selectedPresetId);
      if (preset) {
        applyPresetColors(preset.colors, preset.id);
        return;
      }
    }
    // Pokud není vybrán preset, načíst defaultní
    await loadDefaultPreset();
    // Také zavolat onReset z props
    onReset();
  };


  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-boho-cream">{t('settings.colorLayers.title')}</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={handleReset}
          className="text-xs bg-white/5 border-white/10 text-boho-cream hover:bg-white/10 hover:text-white"
          disabled={isLoadingDefault}
        >
          {isLoadingDefault ? t('common.loading') : t('settings.colorLayers.reset')}
        </Button>
      </div>

      {/* Správa presetů */}
      <div className="space-y-3 border-t border-white/10 pt-3">
        <div className="space-y-2">
          <Label className="text-xs font-medium text-boho-cream">{t('settings.colorLayers.loadPreset')}</Label>
          <div className="flex gap-2">
            <Select
              value={selectedPresetId}
              onValueChange={handleLoadPreset}
              disabled={isLoading}
            >
              <SelectTrigger className="flex-1 text-xs h-8 bg-black/30 border-white/10 text-boho-cream">
                <SelectValue placeholder={isLoading ? t('common.loading') : t('settings.colorLayers.selectPreset')} />
              </SelectTrigger>
              <SelectContent>
                {presets.map((preset) => (
                  <SelectItem key={preset.id} value={preset.id}>
                    {preset.name}
                    {preset.is_default && t('settings.colorLayers.defaultLabel')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <Accordion type="multiple" className="w-full">
        {/* Vodní plochy */}
        <AccordionItem value="water" className="border-white/10">
          <AccordionTrigger className="text-sm text-boho-cream hover:no-underline">{t('settings.colorLayers.sections.water')}</AccordionTrigger>
          <AccordionContent className="space-y-3 pt-2">
            <ColorPickerRow
              label={t('settings.colorLayers.labels.water')}
              color={localColors.water}
              onChange={(c) => handleLayerColorChange('water', c)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Země a pozadí */}
        <AccordionItem value="land" className="border-white/10">
          <AccordionTrigger className="text-sm text-boho-cream hover:no-underline">{t('settings.colorLayers.sections.land')}</AccordionTrigger>
          <AccordionContent className="space-y-3 pt-2">
            <ColorPickerRow
              label={t('settings.colorLayers.labels.mapBackground')}
              color={localColors.background}
              onChange={(c) => handleLayerColorChange('background', c)}
            />
            <ColorPickerRow
              label={t('settings.colorLayers.labels.land')}
              color={localColors.land}
              onChange={(c) => handleLayerColorChange('land', c)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Zelené plochy */}
        <AccordionItem value="green" className="border-white/10">
          <AccordionTrigger className="text-sm text-boho-cream hover:no-underline">{t('settings.colorLayers.sections.green')}</AccordionTrigger>
          <AccordionContent className="space-y-3 pt-2">
            <ColorPickerRow
              label={t('settings.colorLayers.labels.parks')}
              color={localColors.park}
              onChange={(c) => handleLayerColorChange('park', c)}
            />
            <ColorPickerRow
              label={t('settings.colorLayers.labels.forests')}
              color={localColors.forest}
              onChange={(c) => handleLayerColorChange('forest', c)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Budovy */}
        <AccordionItem value="buildings" className="border-white/10">
          <AccordionTrigger className="text-sm text-boho-cream hover:no-underline">{t('settings.colorLayers.sections.buildings')}</AccordionTrigger>
          <AccordionContent className="space-y-3 pt-2">
            <ColorPickerRow
              label={t('settings.colorLayers.labels.buildingFill')}
              color={localColors.building}
              onChange={(c) => handleLayerColorChange('building', c)}
            />
            <ColorPickerRow
              label={t('settings.colorLayers.labels.buildingOutline')}
              color={localColors.building_outline}
              onChange={(c) => handleLayerColorChange('building_outline', c)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Silnice */}
        <AccordionItem value="roads" className="border-white/10">
          <AccordionTrigger className="text-sm text-boho-cream hover:no-underline">{t('settings.colorLayers.sections.roads')}</AccordionTrigger>
          <AccordionContent className="space-y-3 pt-2">
            <ColorPickerRow
              label={t('settings.colorLayers.labels.motorway')}
              color={localColors.road_motorway}
              onChange={(c) => handleLayerColorChange('road_motorway', c)}
            />
            <ColorPickerRow
              label={t('settings.colorLayers.labels.majorRoad')}
              color={localColors.road_major}
              onChange={(c) => handleLayerColorChange('road_major', c)}
            />
            <ColorPickerRow
              label={t('settings.colorLayers.labels.minorRoad')}
              color={localColors.road_minor}
              onChange={(c) => handleLayerColorChange('road_minor', c)}
            />
            <ColorPickerRow
              label={t('settings.colorLayers.labels.paths')}
              color={localColors.road_path}
              onChange={(c) => handleLayerColorChange('road_path', c)}
            />
          </AccordionContent>
        </AccordionItem>

        {/* Popisky */}
        <AccordionItem value="labels" className="border-white/10">
          <AccordionTrigger className="text-sm text-boho-cream hover:no-underline">{t('settings.colorLayers.sections.labels')}</AccordionTrigger>
          <AccordionContent className="space-y-3 pt-2">
            <ColorPickerRow
              label={t('settings.colorLayers.labels.textColor')}
              color={localColors.label_text}
              onChange={(c) => handleLayerColorChange('label_text', c)}
            />
            <ColorPickerRow
              label={t('settings.colorLayers.labels.textHalo')}
              color={localColors.label_halo}
              onChange={(c) => handleLayerColorChange('label_halo', c)}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="text-xs text-boho-cream/90 bg-white/5 p-3 rounded-md">
        {t('settings.colorLayers.tip')}
      </div>
    </div>
  );
}

// Helper komponenta pro řádek s color pickerem
function ColorPickerRow({
  label,
  color,
  onChange
}: {
  label: string;
  color: string;
  onChange: (color: string) => void;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const scrollableParentsRef = useRef<Element[]>([]);

  // Detekce mobilního zařízení
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();

      if (isMobile) {
        // Na mobilu centrovaný modal
        setPosition({
          top: window.innerHeight / 2,
          left: window.innerWidth / 2,
        });
      } else {
        // Na desktopu vedle tlačítka
        // Používáme getBoundingClientRect() bez scroll offsetu, protože fixed positioning je relativní k viewportu
        const padding = 8;
        setPosition({
          top: rect.top,
          left: rect.right + padding,
        });
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    let rafId: number | null = null;
    const handleScroll = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        updatePosition();
      });
    };

    if (isOpen) {
      // Použít mousedown i touchstart pro mobil
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      if (!isMobile) {
        // Přidat listener na scroll pro window i všechny scrollovatelné rodičovské elementy
        window.addEventListener('scroll', handleScroll, true);
        // Najít všechny scrollovatelné rodičovské elementy a uložit reference
        scrollableParentsRef.current = [];
        let parent = buttonRef.current?.parentElement;
        while (parent) {
          const overflow = window.getComputedStyle(parent).overflow;
          if (overflow === 'auto' || overflow === 'scroll' || overflow === 'overlay') {
            scrollableParentsRef.current.push(parent);
            parent.addEventListener('scroll', handleScroll, true);
          }
          parent = parent.parentElement;
        }
      }
      window.addEventListener('resize', updatePosition);
      updatePosition();
    }

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      if (!isMobile) {
        window.removeEventListener('scroll', handleScroll, true);
        // Odstranit listenery z rodičovských elementů pomocí uložených referencí
        scrollableParentsRef.current.forEach(parent => {
          parent.removeEventListener('scroll', handleScroll, true);
        });
        scrollableParentsRef.current = [];
      }
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, isMobile]);

  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <Label className="text-xs flex-1 text-boho-cream">{label}</Label>
        <div className="flex items-center gap-2 relative">
          {/* Color preview */}
          <button
            ref={buttonRef}
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="w-8 h-8 rounded border-2 border-border cursor-pointer hover:border-foreground/50 transition-colors"
            style={{ backgroundColor: color }}
            title={color}
            aria-label={t('settings.colorLayers.selectColorFor', { label })}
          />
        </div>
      </div>
      {/* Shadcn Color Picker Popup - vykreslený přes Portal */}
      {isOpen && typeof window !== 'undefined' && createPortal(
        <>
          {/* Backdrop pro mobil */}
          {isMobile && (
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10000]"
              onClick={() => setIsOpen(false)}
            />
          )}
          <div
            ref={popoverRef}
            className={isMobile ? "fixed z-[10001]" : "fixed z-[9999]"}
            style={isMobile ? {
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              maxWidth: '90vw',
              maxHeight: '90vh',
            } : {
              top: `${position.top}px`,
              left: `${position.left}px`,
            }}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <ColorPicker
              value={color}
              onChange={onChange}
              className={`${isMobile ? 'w-full max-w-sm' : 'max-w-sm'} rounded-md border border-white/10 bg-[#1e1e1e]/95 backdrop-blur-lg p-4 shadow-lg text-boho-cream`}
            >
              <div className={isMobile ? "h-48 w-full" : "h-32 w-full"}>
                <ColorPickerSelection />
              </div>
              <div className={`flex ${isMobile ? 'flex-col gap-2' : 'items-center gap-4'}`}>
                <ColorPickerEyeDropper />
                <ColorPickerSwatch />
                <div className="grid w-full gap-1">
                  <ColorPickerHue />
                  <ColorPickerAlpha />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <ColorPickerOutput />
                <ColorPickerFormat />
              </div>
              {isMobile && (
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => setIsOpen(false)}
                    className="px-4 py-2 bg-green-dark text-white-pure rounded-lg font-semibold"
                  >
                    {t('common.apply', { defaultValue: 'Aplikovat' })}
                  </button>
                </div>
              )}
            </ColorPicker>
          </div>
        </>,
        document.body
      )}
    </>
  );
}

